import { computed, EventEmitter, Injectable, signal, Signal } from '@angular/core';

import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { Observable, BehaviorSubject, ReplaySubject, fromEvent, merge, of } from 'rxjs';
import { filter, startWith, map, share, distinctUntilChanged, tap, catchError } from 'rxjs/operators';

import { AuthenticationService } from '@sondermind/data-access/auth';
import { LocationStore } from '@sondermind/data-access/location';
import { ServerResponse } from '@sondermind/utilities/models-http';
import { Locations } from '@sondermind/utilities/models-location';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';

@Injectable()
export abstract class SondermindApplicationService {
  appInitializing = true;
  appInitializedSubj = new ReplaySubject<boolean>(1);
  appInitialized$ = this.appInitializedSubj.asObservable();

  pageLoaded = false;
  appdown = false;
  minimizeLeftMenu = false;
  hideLeftMenu = false;
  hideTopMenu = false;
  signUpWizard = false;
  sbCountDueInWeek = 0;
  appMode = null;
  navigateSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  shouldShowTopBarSubject: BehaviorSubject<boolean> = new BehaviorSubject(true);
  shouldShowTopBar$: Observable<boolean> = this.shouldShowTopBarSubject.asObservable();

  locations: Locations;

  errorsUpdated = new EventEmitter<any>();
  superBillTabUpdated = new EventEmitter<boolean>();

  private onlineState$ = merge(
    fromEvent(window, 'online'),
    fromEvent(window, 'offline')
  ).pipe(
    map(() => navigator.onLine),
    startWith(navigator.onLine),
    distinctUntilChanged(),
    share(),
  );

  online$ = this.onlineState$.pipe(filter((state) => state), map(() => undefined));
  offline$ = this.onlineState$.pipe(filter((state) => !state), map(() => undefined));

  readonly isSaasProvider: Signal<boolean> = computed(() => this.currentUser.isSaasEnabled());

  readonly hasTimezoneChanged = signal<boolean>(false);

  constructor(
    private authenticationService: AuthenticationService,
    protected locationStore: LocationStore,
    private notificationService: ToastrService,
  ) {
    this.locations = {};
  }

  get currentUser(): ProviderUser {
    return this.authenticationService.user;
  }

  get zoneAbbr(): string {
    return DateTime.now().setZone(this.authenticationService.user.timeZone).toFormat('ZZZZ');
  }

  get currentUser$(): Observable<ProviderUser> {
    return this.authenticationService.user$.pipe(
      distinctUntilChanged()
    );
  }

  get acceptingClients(): boolean {
    return this.authenticationService.user.acceptingClients;
  }

  set acceptingClients(status: boolean) {
    this.authenticationService.user.acceptingClients = status;
  }

  /** @deprecated use authService.loggedIn */
  get hasCurrentUser(): boolean {
    return this.authenticationService.loggedIn;
  }

  /** @deprecated use currentUser.id */
  get currentUserId(): string {
    return `${this.currentUser.id}`;
  }

  get patientDocumentsFeatureGate(): boolean {
    return this.authenticationService.loggedIn && this.currentUser.featureAsBoolean('patientDocuments');
  }

  get providerMessagingFeatureEnabled(): boolean {
    if (!this.authenticationService.loggedIn) { return null }

    return this.currentUser.featureAsBoolean('providerMessagingVersion');
  }

  get loading(): boolean {
    return !this.pageLoaded;
  }

  get shouldShowLeftNav(): boolean {
    return this.pageLoaded && !this.appInitializing && !this.hideLeftMenu;
  }

  get shouldShowTopBar(): boolean {
    return this.pageLoaded && !this.appInitializing && !this.hideTopMenu;
  }

  get shouldShowContent(): boolean {
    return !this.appInitializing;
  }

  currentNavigateSubject(): BehaviorSubject<any> {
    return this.navigateSubject;
  }

  successMessage(message: string, title?: string, overrides?: any): void {
    this.notificationService.success(message, title, overrides);
  }

  errorMessage(message: string, title?: string, overrides?: any): void {
    this.notificationService.error(message, title, overrides);
  }

  alertMessage(message: string, title?: string, overrides?: any): void {
    this.notificationService.error(message, title, overrides);
  }

  warningMessage(message: string, title?: string, overrides?: any): void {
    this.notificationService.warning(message, title, overrides);
  }

  infoMessage(message: string, title?: string, overrides?: any): void {
    this.notificationService.info(message, title, overrides);
  }

  publishErrors(serverResponse: ServerResponse): void {
    this.errorsUpdated.emit(serverResponse);
  }

  // Convert date to MM/DD/YYYY
  serverdate_to_date(d: any): string {
    let date = null;

    if (d instanceof Date) {
      date = d;
    } else if (typeof d === 'string') {
      date = new Date(d);
    }

    if (date === null) {
      return null;
    }

    let jdate = date.toJSON();
    jdate = date.toJSON().substring(0, jdate.indexOf('T')).split('-');
    return `${jdate[1]}/${jdate[2]}/${jdate[0]}`;
  }

  date_to_serverdate(d: any): string {
    let date = null;

    if (d instanceof Date) {
      date = d;
    } else if (typeof d === 'string') {
      date = new Date(d);
    }

    if (date === null) {
      return null;
    }

    const jdate = date.toJSON();
    return jdate.substring(0, jdate.indexOf('T'));
  }

  // This method will convert local/system date object to user's timezone date string
  // then return the Date object of that date string
  userTzDateAslocalDate(date: Date): Date {
    const userTzDateString = date.toLocaleString('en-US', { timeZone: this.currentUser.timeZone });
    const localDate = new Date(userTzDateString);

    return localDate;
  }

  init_app(): Observable<boolean> {
    return this.authenticationService.initialize().pipe(
      tap(() => {
        this.appInitializing = false;
        this.appInitializedSubj.next(true);
      }),
      map(() => true),
      catchError((ex) => {
        // eslint-disable-next-line no-console
        console.error(ex);
        return of(false);
      })
    );
  }

  featureEnabled(name: string): boolean {
    if (this.currentUser) {
      return this.currentUser.featureEnabled(name);
    }
    return false;
  }

  featureAsBoolean(name: string): boolean {
    if (this.currentUser) {
      return this.currentUser.featureAsBoolean(name);
    }
    return false;
  }

  featureAsString(name: string): string {
    if (this.currentUser) {
      return this.currentUser.featureAsString(name);
    }
    return '';
  }

  featureAsNumber(name: string): number {
    return Number.parseFloat(this.featureAsString(name));
  }

  containsTrue(obj: Object): boolean {
    // eslint-disable-next-line no-restricted-syntax
    for (const prop in obj) {
      if (obj[prop] === true) {
        return true;
      }
    }
    return false;
  }

  setSuperBillTab(): void {
    this.superBillTabUpdated.emit(true);
  }

  loadCurrentUser(): Observable<ProviderUser> {
    return this.authenticationService.loadCurrentUser();
  }

  reloadCurrentUser(): Observable<void> {
    return this.authenticationService.reloadCurrentUser();
  }

  setTimezoneHasChanged(value: boolean): void {
    this.hasTimezoneChanged.set(value);
  }
}
